import { useCallback } from "react";
import { useSelector } from "react-redux";

import type { SlotId } from "@skilitics/nautilus-core-designer-data";

import { getDesignerNodeByIdSelector, getPlaceholderForSlotIdSelector, useMutateNodeDesigns } from "../state/index.ts";

export const useSlotDesigner = (slotId: SlotId) => {
  const { remove, reorder } = useMutateNodeDesigns();
  const getById = useSelector(getDesignerNodeByIdSelector);
  const getPlaceholder = useSelector(getPlaceholderForSlotIdSelector);

  const parent = getById(slotId.parentId);
  const placeholder = getPlaceholder(slotId);
  const placeholderId = placeholder?.id;

  let child = parent.childNodes[slotId.name];
  if (Array.isArray(child)) {
    const { index } = slotId;
    if (index === undefined) {
      throw new Error(`Specify index for multi-value slots.`);
    }
    child = child[index];
  }

  if (!placeholderId && !child) {
    throw new Error(
      `SlotDesigner not for use on empty slots. Expected placeholder or initial resident. SlotId: ${slotId.parentId}:${slotId.name}`,
    );
  }

  const resident = child?.id !== placeholderId ? child : undefined;

  const removeSlotResident = useCallback(() => {
    if (resident) {
      remove(resident.id);
    }
  }, [remove, resident]);

  const removeSlot = useCallback(() => {
    removeSlotResident();
    if (placeholderId) {
      remove(placeholderId);
    }
  }, [placeholderId, remove, removeSlotResident]);

  const reorderSlot = useCallback(
    (toIndex: number) => {
      const id = resident?.id ?? placeholderId;
      if (!id) {
        return;
      }
      reorder(id, toIndex);
    },
    [placeholderId, reorder, resident?.id],
  );

  return {
    parent,
    placeholder, // was non-optional. maybe better to generate a generic default placeholder?
    resident,
    removeSlot,
    removeSlotResident,
    reorderSlot,
  };
};
